import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useModuleLicense } from '../hooks/useModuleLicense';
import PageHeader from '../components/PageHeader';
import apiService from '../services/api';
import userService from '../services/user';
import UserProfileEdit from '../components/admin/UserProfileEdit';
import LeaveBalanceEditor from '../components/admin/LeaveBalanceEditor';
import UserScheduleEditor from '../components/admin/UserScheduleEditor';
import CertificationsList from '../components/certifications/CertificationsList';
import EmployeeHoursReport from '../components/hr/EmployeeHoursReport';
import TilAgreementCard from '../components/hr/TilAgreementCard';
import AwardPanel from '../components/hr/AwardPanel';
import timeInLieuService, { type TilAgreement } from '../services/timeInLieu';
import { Card, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import { UserCog, DollarSign, Scale, Clock, ClipboardList, ShieldOff, ShieldCheck, BarChart3, Award } from 'lucide-react';

interface OrgUser {
  id: number;
  username: string;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  organisation_id?: number | null;
  is_active?: boolean;
}

type TabKey = 'payroll' | 'award' | 'leave' | 'schedule' | 'certifications' | 'hours';

const TABS: { key: TabKey; label: string; icon: typeof DollarSign }[] = [
  { key: 'payroll', label: 'Payroll', icon: DollarSign },
  { key: 'award', label: 'Award', icon: Award },
  { key: 'leave', label: 'Leave entitlements', icon: Scale },
  { key: 'schedule', label: 'Schedule', icon: Clock },
  { key: 'certifications', label: 'Certifications', icon: ClipboardList },
  { key: 'hours', label: 'Hours report', icon: BarChart3 },
];

function displayName(u: { first_name?: string | null; last_name?: string | null; username: string }): string {
  const full = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
  return full || u.username;
}

export default function StaffProfile() {
  const { user } = useAuth();
  const { hasAccess: hasHRAccess, loading: hrLoading } = useModuleLicense('timesheet', 'hr');

  const [users, setUsers] = useState<OrgUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState<string | null>(null);

  // The tab rides in ?tab= so the old /hr/leave-entitlements URL can land on Leave.
  const [searchParams, setSearchParams] = useSearchParams();
  // ?user= opens on that person: a time in lieu notification links here.
  const [selectedUserId, setSelectedUserId] = useState<number | null>(() => {
    const u = Number(searchParams.get('user'));
    return Number.isInteger(u) && u > 0 ? u : null;
  });
  const tabParam = searchParams.get('tab');
  const activeTab: TabKey = TABS.some(t => t.key === tabParam) ? (tabParam as TabKey) : 'payroll';
  const setActiveTab = (tab: TabKey) => setSearchParams({ tab }, { replace: true });
  const [showInactive, setShowInactive] = useState(false);

  // Employment status changes
  const [confirmingStatus, setConfirmingStatus] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [statusNotice, setStatusNotice] = useState<string | null>(null);

  // Time in lieu requests waiting for an HR decision, across the organisation.
  const [tilPending, setTilPending] = useState<TilAgreement[]>([]);
  const [tilRefresh, setTilRefresh] = useState(0);
  useEffect(() => {
    if (!hasHRAccess) return;
    let cancelled = false;
    timeInLieuService.list().then(res => {
      if (!cancelled && res.success && res.data) setTilPending(res.data.filter(r => r.status === 'pending'));
    }).catch(() => { /* the list is a convenience; each person's card says the same */ });
    return () => { cancelled = true; };
  }, [hasHRAccess, tilRefresh]);

  const selectedUser = useMemo(
    () => users.find(u => u.id === selectedUserId) || null,
    [users, selectedUserId]
  );

  // ----- Initial user list (org-scoped) -----
  useEffect(() => {
    if (!hasHRAccess) {
      setUsersLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setUsersLoading(true);
        setUsersError(null);
        const response = await apiService.get<OrgUser[]>(
          `/users?limit=200${showInactive ? '&include_inactive=true' : ''}`
        );
        if (cancelled) return;
        if (response.success && Array.isArray(response.data)) {
          const orgId = user?.organisation_id;
          const orgUsers = (orgId
            ? response.data.filter(u => u.organisation_id === orgId)
            : response.data
          ).filter(u => showInactive || u.is_active !== false);
          orgUsers.sort((a, b) => displayName(a).localeCompare(displayName(b)));
          setUsers(orgUsers);
        } else {
          setUsers([]);
        }
      } catch (err) {
        if (!cancelled) {
          setUsersError((err as Error).message || 'Failed to load users');
          setUsers([]);
        }
      } finally {
        if (!cancelled) setUsersLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [hasHRAccess, user?.organisation_id, showInactive]);

  // Reset the status controls whenever a different employee is picked
  useEffect(() => {
    setConfirmingStatus(false);
    setStatusError(null);
    setStatusNotice(null);
  }, [selectedUserId]);

  const changeStatus = async (target: OrgUser, makeActive: boolean) => {
    try {
      setStatusSaving(true);
      setStatusError(null);
      setStatusNotice(null);
      const response = await userService.setUserActiveStatus(target.id, makeActive);
      if (!response.success) {
        setStatusError(`Failed to ${makeActive ? 'reactivate' : 'deactivate'} this employee.`);
        return;
      }
      // The status is this organisation's. The server signs them out only when
      // this was the last organisation they had; someone who also works for
      // another organisation stays signed in there.
      const stillElsewhere = response.data?.account_active === true;
      setUsers(prev => prev.map(u => (u.id === target.id ? { ...u, is_active: makeActive } : u)));
      setConfirmingStatus(false);
      setStatusNotice(
        makeActive
          ? `${displayName(target)} has been reactivated.`
          : stillElsewhere
            ? `${displayName(target)} has been made inactive here. They also belong to another organisation and can still sign in to it.`
            : `${displayName(target)} has been made inactive and signed out of all devices.`
      );
    } catch (err) {
      const { status, message } = (err ?? {}) as { status?: number; message?: string };
      setStatusError(
        status === 403
          ? 'You do not have permission to change account status. An administrator is required.'
          : message || `Failed to ${makeActive ? 'reactivate' : 'deactivate'} this employee.`
      );
    } finally {
      setStatusSaving(false);
    }
  };

  // ----- Guards (after all hooks) -----
  if (hrLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-neutral-500">Loading…</p>
      </div>
    );
  }

  if (!hasHRAccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <UserCog className="h-12 w-12 text-neutral-300 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-neutral-700">Access Restricted</h2>
          <p className="text-sm text-neutral-500 mt-1">A Timesheet HR license seat is required to access employee profiles.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Employee Profile"
        subtitle="One place to manage an employee's payroll details, leave entitlements, work schedule, and certifications"
        icon={<UserCog className="h-6 w-6 text-white" />}
      />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">

        {/* Employee picker */}
        <div className="bg-white border border-neutral-200 rounded-xl p-6">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
            <label htmlFor="employee-select" className="block text-sm font-medium text-neutral-700">
              Select employee
            </label>
            <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-neutral-600">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={e => setShowInactive(e.target.checked)}
                className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
              />
              Show inactive employees
            </label>
          </div>
          {usersLoading ? (
            <div className="text-sm text-neutral-500">Loading employees…</div>
          ) : usersError ? (
            <div className="text-sm text-red-600">{usersError}</div>
          ) : (
            <select
              id="employee-select"
              className="w-full max-w-md border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
              value={selectedUserId ?? ''}
              onChange={e => setSelectedUserId(e.target.value ? parseInt(e.target.value, 10) : null)}
            >
              <option value="">— Choose an employee —</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>
                  {displayName(u)} ({u.email}){u.is_active === false ? ' — inactive' : ''}
                </option>
              ))}
            </select>
          )}
        </div>

        {tilPending.length > 0 && (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900 space-y-2">
            <p className="font-semibold">
              {tilPending.length === 1 ? '1 person has' : `${tilPending.length} people have`} asked for time in lieu instead of paid overtime
            </p>
            <ul className="space-y-1">
              {tilPending.map(r => (
                <li key={r.id} className="flex flex-wrap items-center gap-2">
                  <span className="min-w-0 break-words">{r.employee_name || r.employee_username}</span>
                  <button
                    type="button"
                    className="text-primary-700 hover:underline"
                    onClick={() => { setSelectedUserId(r.user_id); setActiveTab('payroll'); }}
                  >
                    Review
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {statusNotice && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
            {statusNotice}
          </div>
        )}
        {statusError && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {statusError}
          </div>
        )}

        {selectedUser && (
          <>
            {/* Employment status */}
            <div className="bg-white border border-neutral-200 rounded-xl p-5 flex items-center justify-between flex-wrap gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-neutral-700">Account status</span>
                  {selectedUser.is_active === false ? (
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-neutral-200 text-neutral-600 flex items-center gap-1">
                      <ShieldOff className="h-3 w-3" /> Inactive
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 flex items-center gap-1">
                      <ShieldCheck className="h-3 w-3" /> Active
                    </span>
                  )}
                </div>
                <p className="text-xs text-neutral-500 mt-1">
                  {selectedUser.is_active === false
                    ? 'This employee has no access to this organisation. Their historical timesheets and records are kept.'
                    : 'Making an employee inactive removes their access to this organisation only. Their records are kept, and any other organisation they belong to is unaffected.'}
                </p>
              </div>

              <div className="shrink-0 flex items-center gap-2">
                {selectedUser.is_active === false ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => changeStatus(selectedUser, true)}
                    disabled={statusSaving}
                    className="flex items-center gap-1 border-green-300 text-green-700 hover:bg-green-50"
                  >
                    <ShieldCheck className="h-3.5 w-3.5" />
                    {statusSaving ? 'Saving…' : 'Reactivate'}
                  </Button>
                ) : confirmingStatus ? (
                  <>
                    <span className="text-sm text-red-700 font-medium">Make this employee inactive?</span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => changeStatus(selectedUser, false)}
                      disabled={statusSaving}
                      className="border-red-300 text-red-700 hover:bg-red-50"
                    >
                      {statusSaving ? 'Saving…' : 'Confirm'}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setConfirmingStatus(false)}
                      disabled={statusSaving}
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setStatusError(null); setStatusNotice(null); setConfirmingStatus(true); }}
                    className="flex items-center gap-1 border-red-200 text-red-600 hover:bg-red-50"
                  >
                    <ShieldOff className="h-3.5 w-3.5" />
                    Make inactive
                  </Button>
                )}
              </div>
            </div>

            {/* Tab bar */}
            <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
              <div className="flex flex-wrap border-b border-neutral-200" role="tablist">
                {TABS.map(tab => {
                  const Icon = tab.icon;
                  const active = activeTab === tab.key;
                  return (
                    <button
                      key={tab.key}
                      role="tab"
                      aria-selected={active}
                      onClick={() => setActiveTab(tab.key)}
                      className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                        active
                          ? 'border-primary-600 text-primary-700 bg-primary-50/50'
                          : 'border-transparent text-neutral-500 hover:text-neutral-800 hover:bg-neutral-50'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      {tab.label}
                    </button>
                  );
                })}
              </div>
              <div className="px-5 py-3 text-sm text-neutral-500 break-words">
                Editing <span className="font-semibold text-neutral-800">{displayName(selectedUser)}</span>
                <span className="text-neutral-400"> ({selectedUser.email})</span>
              </div>
            </div>

            {/* Tab panels — keyed by user so they reload on employee change */}
            <div key={selectedUser.id}>
              {activeTab === 'payroll' && (
                <div className="space-y-6">
                  <TilAgreementCard userId={selectedUser.id} onChanged={() => setTilRefresh(n => n + 1)} />
                  <UserProfileEdit userId={selectedUser.id} />
                </div>
              )}

              {activeTab === 'award' && (
                <AwardPanel userId={selectedUser.id} userName={displayName(selectedUser)} />
              )}

              {activeTab === 'leave' && (
                <LeaveBalanceEditor userId={selectedUser.id} userDisplayName={displayName(selectedUser)} />
              )}

              {activeTab === 'schedule' && (
                <Card>
                  <CardContent className="pt-6">
                    <UserScheduleEditor userId={selectedUser.id} userName={displayName(selectedUser)} />
                  </CardContent>
                </Card>
              )}

              {activeTab === 'certifications' && (
                <Card>
                  <CardContent className="pt-6">
                    <CertificationsList userId={selectedUser.id} typeModuleFilter="any" />
                  </CardContent>
                </Card>
              )}

              {activeTab === 'hours' && (
                <EmployeeHoursReport userId={selectedUser.id} userDisplayName={displayName(selectedUser)} />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
